import React, { useRef, useEffect, useState, useCallback } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { LightMode, Model } from "../../types";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";

const SCENE_BACKGROUND = {
  light: 0x8fa6bb,
  dark: 0x8fa6bb,
} as const;

const RING_COUNT = 8;

interface SurroundLevel {
  ambient: number;
  equator: number;
  upper: number;
  top: number;
  bottom: number;
}

const SURROUND_LEVELS: Record<LightMode, SurroundLevel> = {
  studio: { ambient: 0.42, equator: 0.34, upper: 0.3, top: 0.46, bottom: 0.18 },
  top: { ambient: 0.4, equator: 0.24, upper: 0.42, top: 0.85, bottom: 0.12 },
  side: { ambient: 0.32, equator: 0.52, upper: 0.26, top: 0.28, bottom: 0.16 },
};

const SURROUND_POSITIONS: [number, number, number][] = (() => {
  const positions: [number, number, number][] = [];
  for (let i = 0; i < RING_COUNT; i += 1) {
    const angle = (i / RING_COUNT) * Math.PI * 2;
    positions.push([Math.cos(angle) * 10, 2.4, Math.sin(angle) * 10]);
  }
  for (let i = 0; i < RING_COUNT; i += 1) {
    const angle = ((i + 0.5) / RING_COUNT) * Math.PI * 2;
    positions.push([Math.cos(angle) * 7, 9, Math.sin(angle) * 7]);
  }
  positions.push([0, 16, 0]);
  positions.push([0, -8, 0]);
  return positions;
})();

const intensitiesForMode = (mode: LightMode): number[] => {
  const level = SURROUND_LEVELS[mode];
  return SURROUND_POSITIONS.map((_, index) => {
    if (index < RING_COUNT) return level.equator;
    if (index < RING_COUNT * 2) return level.upper;
    if (index === RING_COUNT * 2) return level.top;
    return level.bottom;
  });
};

const applySurround = (
  mode: LightMode,
  ambient: THREE.AmbientLight,
  lights: THREE.DirectionalLight[],
  scale = 1
) => {
  ambient.color.setHex(0xffffff);
  ambient.intensity = SURROUND_LEVELS[mode].ambient * scale;
  const intensities = intensitiesForMode(mode);
  lights.forEach((light, index) => {
    const position = SURROUND_POSITIONS[index];
    light.color.setHex(index === lights.length - 1 ? 0xf3f6fb : 0xffffff);
    light.intensity = intensities[index] * scale;
    light.position.set(position[0], position[1], position[2]);
  });
};

export interface VanillaThreeViewerProps {
  models: Model[];
  currentTool: "pencil" | null;
  onClearDrawing: () => void;
  onTakeScreenshot: () => void;
  drawingColor: string;
  lightMode: LightMode;
  lightIntensity: number;
}

const VanillaThreeViewer: React.FC<VanillaThreeViewerProps> = ({
  models,
  currentTool,
  onClearDrawing,
  onTakeScreenshot,
  drawingColor,
  lightMode,
  lightIntensity,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const ambientLightRef = useRef<THREE.AmbientLight | null>(null);
  const surroundLightsRef = useRef<THREE.DirectionalLight[]>([]);
  const modelObjectsRef = useRef<{ [key: string]: THREE.Object3D }>({});
  const animationIdRef = useRef<number | null>(null);

  // РЕШЕНИЕ: Ref для хранения актуального состояния моделей
  const modelsRef = useRef(models);
  useEffect(() => {
    modelsRef.current = models;
  }, [models]);

  const [isLoading, setIsLoading] = useState(false);
  const loadingModelsRef = useRef(new Set<string>());

  const gltfLoaderRef = useRef<GLTFLoader | null>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const currentLineRef = useRef<THREE.Object3D | null>(null);
  const linePointsRef = useRef<THREE.Vector3[]>([]);
  const allLinesRef = useRef<THREE.Object3D[]>([]);
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());

  const LINE_OFFSET = 0.2;
  const LINE_THICKNESS = 2.0;

  const { isDark } = useTheme();
  const { t } = useLanguage();
  const baseUrl =
    process.env.REACT_APP_API_URL || "https://bone.implexvet.com/api";

  useEffect(() => {
    if (!mountRef.current) return;
    const mountNode = mountRef.current;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      60,
      mountNode.offsetWidth / mountNode.offsetHeight,
      0.1,
      10000
    );
    camera.position.set(700, 0, 0);
    camera.rotation.z = Math.PI;
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true,
    });
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.setSize(mountNode.offsetWidth, mountNode.offsetHeight);
    mountNode.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.enablePan = true;
    controls.zoomSpeed = 0.3;
    controls.screenSpacePanning = true;
    controlsRef.current = controls;

    const initialBackground = document.body.classList.contains("dark-theme")
      ? SCENE_BACKGROUND.dark
      : SCENE_BACKGROUND.light;
    scene.background = new THREE.Color(initialBackground);
    renderer.setClearColor(initialBackground, 1);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.42);
    const surroundLights = SURROUND_POSITIONS.map(
      () => new THREE.DirectionalLight(0xffffff, 0)
    );
    applySurround("studio", ambientLight, surroundLights);
    scene.add(ambientLight, ...surroundLights);
    ambientLightRef.current = ambientLight;
    surroundLightsRef.current = surroundLights;

    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current || !mountRef.current)
        return;
      const { offsetWidth, offsetHeight } = mountRef.current;
      cameraRef.current.aspect = offsetWidth / offsetHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(offsetWidth, offsetHeight);
    };
    window.addEventListener("resize", handleResize);

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("https://www.gstatic.com/draco/v1/decoders/");
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);
    gltfLoaderRef.current = loader;

    return () => {
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      window.removeEventListener("resize", handleResize);
      if (mountNode && renderer.domElement) {
        mountNode.removeChild(renderer.domElement);
      }
      renderer.dispose();
      Object.values(modelObjectsRef.current).forEach((obj) =>
        scene.remove(obj)
      );
      allLinesRef.current.forEach((line) => scene.remove(line));
      modelObjectsRef.current = {};
      allLinesRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!sceneRef.current) return;
    const color = isDark ? SCENE_BACKGROUND.dark : SCENE_BACKGROUND.light;
    const previous = sceneRef.current.background;
    sceneRef.current.background = new THREE.Color(color);
    if (previous instanceof THREE.Texture) previous.dispose();
    rendererRef.current?.setClearColor(color, 1);
  }, [isDark]);

  useEffect(() => {
    const ambient = ambientLightRef.current;
    const lights = surroundLightsRef.current;
    if (!ambient || lights.length === 0) return;
    applySurround(lightMode, ambient, lights, lightIntensity);
  }, [lightMode, lightIntensity]);

  // Основной useEffect для синхронизации моделей
  useEffect(() => {
    if (!sceneRef.current || !gltfLoaderRef.current) return;

    const scene = sceneRef.current;
    const loader = gltfLoaderRef.current;

    const modelsMap = new Map(models.map((m) => [m.id, m]));
    const existingModelIds = new Set(Object.keys(modelObjectsRef.current));

    // 1. Удаление моделей, которых больше нет в списке
    existingModelIds.forEach((modelId) => {
      if (!modelsMap.has(modelId)) {
        if (modelObjectsRef.current[modelId]) {
          scene.remove(modelObjectsRef.current[modelId]);
          delete modelObjectsRef.current[modelId];
        }
      }
    });

    // 2. Обновление и загрузка моделей
    models.forEach((model) => {
      const existingObject = modelObjectsRef.current[model.id];

      if (existingObject) {
        existingObject.visible = model.isVisible;
        if (model.canChangeTransparency) {
          existingObject.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
              const mesh = child as THREE.Mesh;
              const updateMaterial = (mat: THREE.Material) => {
                mat.transparent = model.opacity < 1.0;
                mat.opacity = model.opacity;
                mat.needsUpdate = true;
              };
              if (Array.isArray(mesh.material)) {
                mesh.material.forEach(updateMaterial);
              } else if (mesh.material) {
                updateMaterial(mesh.material);
              }
            }
          });
        }
      } else if (model.isVisible && !loadingModelsRef.current.has(model.id)) {
        loadingModelsRef.current.add(model.id);
        setIsLoading(true);

        loader.load(
          `${baseUrl}${model.filePath}`,
          (gltf) => {
            loadingModelsRef.current.delete(model.id);
            if (loadingModelsRef.current.size === 0) {
              setIsLoading(false);
            }

            const latestModels = modelsRef.current;
            const currentModelData = latestModels.find(
              (m) => m.id === model.id
            );

            if (!currentModelData || !currentModelData.isVisible) {
              return;
            }

            const modelObject = gltf.scene.clone();
            modelObject.scale.set(3.5, 3.5, 3.5);
            modelObject.position.set(0, 0, 0);
            modelObject.visible = currentModelData.isVisible;

            modelObject.traverse((child) => {
              if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                const updateMaterial = (mat: THREE.Material) => {
                  mat.transparent = currentModelData.opacity < 1.0;
                  mat.opacity = currentModelData.opacity;
                };
                if (Array.isArray(mesh.material)) {
                  mesh.material.forEach(updateMaterial);
                } else if (mesh.material) {
                  updateMaterial(mesh.material);
                }
              }
            });

            modelObjectsRef.current[model.id] = modelObject;
            if (sceneRef.current) {
              sceneRef.current.add(modelObject);
            }
          },
          undefined,
          (error) => {
            console.error(`Error loading model ${model.name}:`, error);
            loadingModelsRef.current.delete(model.id);
            if (loadingModelsRef.current.size === 0) {
              setIsLoading(false);
            }
          }
        );
      }
    });
  }, [models, baseUrl]);

  const createThickLine = useCallback(
    (points: THREE.Vector3[]): THREE.Object3D | null => {
      if (points.length < 2) return null;
      try {
        const curve = new THREE.CatmullRomCurve3(points);
        const tubeGeometry = new THREE.TubeGeometry(
          curve,
          points.length * 2,
          LINE_THICKNESS,
          8,
          false
        );
        const tubeMaterial = new THREE.MeshBasicMaterial({
          color: new THREE.Color(drawingColor),
          side: THREE.DoubleSide,
        });
        return new THREE.Mesh(tubeGeometry, tubeMaterial);
      } catch (error) {
        console.error("Failed to create thick line:", error);
        return null;
      }
    },
    [drawingColor]
  );

  const getIntersection = useCallback(
    (mousePos: THREE.Vector2): THREE.Intersection | null => {
      if (!cameraRef.current) return null;
      raycaster.current.setFromCamera(mousePos, cameraRef.current);
      const drawTargets = Object.values(modelObjectsRef.current).filter(
        (model) => model.visible
      );
      const intersects = raycaster.current.intersectObjects(drawTargets, true);
      return intersects.length > 0 ? intersects[0] : null;
    },
    []
  );

  useEffect(() => {
    if (!mountRef.current || currentTool !== "pencil") return;
    const mountNode = mountRef.current;

    const getEventPos = (event: MouseEvent | TouchEvent) => {
      const rect = mountNode.getBoundingClientRect();
      let clientX, clientY;
      if ("touches" in event) {
        clientX = event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX;
        clientY = event.touches[0]?.clientY ?? event.changedTouches[0]?.clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      return {
        x: ((clientX - rect.left) / rect.width) * 2 - 1,
        y: -((clientY - rect.top) / rect.height) * 2 + 1,
      };
    };

    const onStart = (event: MouseEvent | TouchEvent) => {
      if (event.type === "mousedown" && (event as MouseEvent).button !== 0)
        return;
      event.preventDefault();
      const pos = getEventPos(event);
      mouse.current.set(pos.x, pos.y);

      const intersection = getIntersection(mouse.current);
      if (intersection) {
        setIsDrawing(true);
        const offsetPoint = intersection.point.clone();
        if (intersection.face) {
          const normal = intersection.face.normal
            .clone()
            .transformDirection(intersection.object.matrixWorld);
          offsetPoint.add(normal.multiplyScalar(LINE_OFFSET));
        }
        linePointsRef.current = [offsetPoint];
      }
    };

    const onMove = (event: MouseEvent | TouchEvent) => {
      if (!isDrawing) return;
      event.preventDefault();
      const pos = getEventPos(event);
      mouse.current.set(pos.x, pos.y);

      const intersection = getIntersection(mouse.current);
      if (intersection && sceneRef.current) {
        if (currentLineRef.current) {
          sceneRef.current.remove(currentLineRef.current);
        }
        const offsetPoint = intersection.point.clone();
        if (intersection.face) {
          const normal = intersection.face.normal
            .clone()
            .transformDirection(intersection.object.matrixWorld);
          offsetPoint.add(normal.multiplyScalar(LINE_OFFSET));
        }
        const newPoints = [...linePointsRef.current, offsetPoint];
        linePointsRef.current = newPoints;
        const line = createThickLine(newPoints);
        if (line) {
          sceneRef.current.add(line);
          currentLineRef.current = line;
        }
      }
    };

    const onEnd = () => {
      if (!isDrawing) return;
      setIsDrawing(false);
      if (sceneRef.current && currentLineRef.current) {
        sceneRef.current.remove(currentLineRef.current);
        if (linePointsRef.current.length > 1) {
          const finalLine = createThickLine(linePointsRef.current);
          if (finalLine) {
            sceneRef.current.add(finalLine);
            allLinesRef.current.push(finalLine);
          }
        }
      }
      currentLineRef.current = null;
      linePointsRef.current = [];
    };

    mountNode.addEventListener("mousedown", onStart);
    mountNode.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    mountNode.addEventListener("touchstart", onStart, { passive: false });
    mountNode.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);

    return () => {
      mountNode.removeEventListener("mousedown", onStart);
      mountNode.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      mountNode.removeEventListener("touchstart", onStart);
      mountNode.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [currentTool, isDrawing, getIntersection, createThickLine]);

  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.enabled = !isLoading && currentTool !== "pencil";
    }
  }, [isLoading, currentTool]);

  useEffect(() => {
    (window as any).clearDrawingFunction = () => {
      if (!sceneRef.current) return;
      allLinesRef.current.forEach((line) => sceneRef.current!.remove(line));
      allLinesRef.current = [];
      if (currentLineRef.current) {
        sceneRef.current.remove(currentLineRef.current);
        currentLineRef.current = null;
      }
      linePointsRef.current = [];
      onClearDrawing();
    };

    (window as any).screenshotFunction = () => {
      if (!rendererRef.current || !sceneRef.current || !cameraRef.current)
        return;
      try {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
        const dataURL = rendererRef.current.domElement.toDataURL("image/png");
        if (dataURL === "data:,") return;
        const link = document.createElement("a");
        link.href = dataURL;
        link.download = `3d-model-screenshot-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        onTakeScreenshot();
      } catch (error) {
        console.error("Screenshot failed:", error);
      }
    };
  }, [onClearDrawing, onTakeScreenshot]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        touchAction: "none",
      }}
    >
      <div ref={mountRef} style={{ width: "100%", height: "100%" }} />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            pointerEvents: "all",
          }}
        >
          <div
            style={{
              background: "var(--bg-primary)",
              padding: "20px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid #f3f3f3",
                borderTop: "2px solid #0099ff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <span>{t("loading")}</span>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VanillaThreeViewer;
